import { fetchAuthSession } from 'aws-amplify/auth';

const API_URL = import.meta.env.VITE_API_URL;

export type ReviewDisplayNameMode = 'account' | 'custom' | 'anonymous';
export type ReviewStatus = 'pending' | 'approved' | 'rejected';

export interface ReviewActivityRef {
  id: string;
  slug: string;
  titleCN: string | null;
  titleEN: string | null;
}

/** A single published review as shown on public pages. */
export interface PublicReview {
  id: string;
  displayName: string;
  content: string | null;
  publishedAt: string | null;
  attendedAt: string | null;
  adminReply: string | null;
  adminReplyAt: string | null;
  activity?: ReviewActivityRef; // present only on host-aggregated lists
}

export interface PublicReviewPage {
  total: number;
  page: number;
  pageSize: number;
  items: PublicReview[];
}

export interface ReviewEligibility {
  eligible: boolean;
  alreadyReviewed: boolean;
  expired: boolean;
  attendedAt: string | null;
}

/** The author's own review (richer than the public shape). */
export interface MyReview {
  id: string;
  content: string;
  publishedContent: string | null;
  status: ReviewStatus;
  rejectionReason: string | null;
  displayNameMode: ReviewDisplayNameMode;
  authorDisplayName: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  adminReply: string | null;
  adminReplyAt: string | null;
  /** Within the 90-day write window (R4/R17): false => edit is no longer allowed. */
  editable: boolean;
  activity: ReviewActivityRef;
}

export interface MyReviewsResult {
  rejectedCount: number;
  items: MyReview[];
}

export interface ReviewWritePayload {
  content: string;
  displayNameMode: ReviewDisplayNameMode;
  customName?: string;
}

/** A review row as seen by an admin (includes author identity + raw content). */
export interface AdminReview {
  id: string;
  content: string;
  publishedContent: string | null;
  status: ReviewStatus;
  rejectionReason: string | null;
  displayNameMode: ReviewDisplayNameMode;
  authorDisplayName: string | null;
  attendedAt: string | null;
  adminReply: string | null;
  adminReplyAt: string | null;
  createdAt: string;
  updatedAt: string;
  publishedAt: string | null;
  user: { id: string; email: string; name: string | null };
  activity: ReviewActivityRef;
}

export interface AdminReviewsResult {
  total: number;
  page: number;
  pageSize: number;
  items: AdminReview[];
}

// --- YOYO Picks reviews (PRD §5.5) ------------------------------------------

export type PickVerdict = 'RECOMMEND' | 'NOT_RECOMMEND';

/** The fields the author edits. */
export interface MyPickReviewDraft {
  verdict: PickVerdict;
  content: string;
  visited: boolean;
  /** Pick type when "visited" was ticked: fixes the badge wording (P1). */
  visitedAs?: 'PROVIDER' | 'EVENT' | null;
  displayNameMode: ReviewDisplayNameMode;
  authorDisplayName: string | null;
}

export interface MyPickReview extends MyPickReviewDraft {
  id: string;
  status: ReviewStatus;
  rejectionReason: string | null;
  updatedAt: string;
  pick: {
    id: string;
    slug: string;
    type: 'PROVIDER' | 'EVENT';
    titleCN: string | null;
    titleEN: string | null;
    status: string;
  };
}

export interface PickReviewWritePayload {
  verdict: PickVerdict;
  content: string;
  visited: boolean;
  displayNameMode: ReviewDisplayNameMode;
  customName?: string;
}

/** Admin queue row for a Pick review (same actions as activity reviews). */
export interface AdminPickReview extends Omit<AdminReview, 'activity' | 'attendedAt'> {
  verdict: PickVerdict;
  visited: boolean;
  pick: ReviewActivityRef;
}

export type ReviewSource = 'activity' | 'pick';
const sourceQuery = (source: ReviewSource) => (source === 'pick' ? '?source=pick' : '');

/** Error carrying the server-provided code so callers can show precise messages. */
export class ReviewApiError extends Error {
  code: string;
  status: number;
  constructor(code: string, status: number, message: string) {
    super(message);
    this.name = 'ReviewApiError';
    this.code = code;
    this.status = status;
  }
}

async function request<T>(endpoint: string, options: RequestInit = {}): Promise<T> {
  let token: string | undefined;
  try {
    const session = await fetchAuthSession();
    token = session.tokens?.idToken?.toString();
  } catch {
    // Public endpoints work without a token.
  }

  const res = await fetch(`${API_URL}${endpoint}`, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    },
  });

  if (res.status === 204) return undefined as T;

  interface Envelope {
    success?: boolean;
    data?: unknown;
    // Our controllers send { error: { code, message } }; some middleware sends
    // { error: "msg" } or { message: "msg" }. Handle all three.
    error?: { code?: string; message?: string } | string;
    message?: string;
  }

  let body: Envelope | null = null;
  try {
    body = (await res.json()) as Envelope;
  } catch {
    // fall through to status-based error
  }

  if (!res.ok || body?.success === false) {
    const err = body?.error;
    const code = typeof err === 'object' && err?.code ? err.code : 'ERROR';
    const message =
      (typeof err === 'string' ? err : err?.message) ?? body?.message ?? `HTTP ${res.status}`;
    throw new ReviewApiError(code, res.status, message);
  }

  return body?.data as T;
}

export const reviewApi = {
  listForActivity: (activityId: string, page = 1, pageSize = 10) =>
    request<PublicReviewPage>(`/reviews/activity/${activityId}?page=${page}&pageSize=${pageSize}`),

  listForHost: (hostId: string, page = 1, pageSize = 10) =>
    request<PublicReviewPage>(`/reviews/host/${hostId}?page=${page}&pageSize=${pageSize}`),

  getEligibility: (activityId: string) =>
    request<ReviewEligibility>(`/reviews/activity/${activityId}/eligibility`),

  create: (activityId: string, payload: ReviewWritePayload) =>
    request<MyReview>(`/reviews/activity/${activityId}`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  update: (reviewId: string, payload: ReviewWritePayload) =>
    request<MyReview>(`/reviews/${reviewId}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  remove: (reviewId: string) => request<void>(`/reviews/${reviewId}`, { method: 'DELETE' }),

  getMine: () => request<MyReviewsResult>(`/reviews/me`),

  // --- YOYO Picks (author) ---
  pickMine: () => request<{ rejectedCount: number; items: MyPickReview[] }>('/picks/my-reviews'),

  pickMineForPick: (pickId: string) =>
    request<(MyPickReviewDraft & { id: string; status: ReviewStatus }) | null>(
      `/picks/${encodeURIComponent(pickId)}/my-review`
    ),

  pickCreate: (pickId: string, payload: PickReviewWritePayload) =>
    request<{ id: string }>(`/picks/${encodeURIComponent(pickId)}/reviews`, {
      method: 'POST',
      body: JSON.stringify(payload),
    }),

  pickUpdate: (reviewId: string, payload: PickReviewWritePayload) =>
    request<{ id: string }>(`/picks/reviews/${encodeURIComponent(reviewId)}`, {
      method: 'PATCH',
      body: JSON.stringify(payload),
    }),

  pickRemove: (reviewId: string) =>
    request<void>(`/picks/reviews/${encodeURIComponent(reviewId)}`, { method: 'DELETE' }),

  // --- Admin (source=pick moderates YOYO Picks reviews) ---
  adminList: (
    status: 'pending' | 'approved' | 'rejected' | 'all' = 'pending',
    page = 1,
    pageSize = 20,
    source: ReviewSource = 'activity'
  ) =>
    request<AdminReviewsResult | { total: number; items: AdminPickReview[] }>(
      `/admin/reviews?status=${status}&page=${page}&pageSize=${pageSize}${
        source === 'pick' ? '&source=pick' : ''
      }`
    ),

  adminApprove: (id: string, source: ReviewSource = 'activity') =>
    request<AdminReview>(`/admin/reviews/${id}/approve${sourceQuery(source)}`, { method: 'POST' }),

  adminReject: (id: string, reason: string, source: ReviewSource = 'activity') =>
    request<AdminReview>(`/admin/reviews/${id}/reject${sourceQuery(source)}`, {
      method: 'POST',
      body: JSON.stringify({ reason }),
    }),

  adminRemove: (id: string, source: ReviewSource = 'activity') =>
    request<void>(`/admin/reviews/${id}${sourceQuery(source)}`, { method: 'DELETE' }),

  adminSetReply: (id: string, reply: string, source: ReviewSource = 'activity') =>
    request<AdminReview>(`/admin/reviews/${id}/reply${sourceQuery(source)}`, {
      method: 'PUT',
      body: JSON.stringify({ reply }),
    }),

  adminDeleteReply: (id: string, source: ReviewSource = 'activity') =>
    request<AdminReview>(`/admin/reviews/${id}/reply${sourceQuery(source)}`, {
      method: 'DELETE',
    }),
};
